import { apiClient } from './api'
import { getTimeZoneIdForRegion } from '@/config/regionTimezones'
import { formatDate as fmtDate } from '@/composables/useFormatDate'
import type {
  Order,
  OrderListItem,
  OrderStatus,
  FulfillmentType,
  OrderStatistics,
  CreateOrderRequest,
  UpdateOrderStatusRequest,
  OrderFilters,
  PaginatedResult,
  PaymentIntentResult,
  CancelOrderResult,
  OrderAvailabilityIssue,
  OrderAvailabilityIssueSummary,
  AvailabilityIssuePublic,
  AvailabilityResponseResult,
  OrderAuditEntry
} from '@/types/shop'

/**
 * Per-line restock instruction sent alongside a refund. Only inventory-tracked
 * items are honoured — non-tracked items are silently ignored server-side.
 * quantity must be > 0 and is capped at the OrderItem's ordered quantity.
 */
export interface RestockItem {
  orderItemId: string
  quantity: number
}

export const shopOrderService = {
  // ============================================================================
  // Provider Order Management
  // ============================================================================

  /**
   * Get orders for a shop with filtering and pagination
   */
  async getOrders(
    shopId: string,
    filters: OrderFilters = {}
  ): Promise<PaginatedResult<OrderListItem>> {
    const params = {
      search: filters.search,
      status: filters.status,
      fulfillmentType: filters.fulfillmentType,
      // Channel filter — values match Zaveato.Domain.Enums.OrderSource string names.
      source: filters.source,
      paymentStatus: filters.paymentStatus,
      hasPendingPayment: filters.hasPendingPayment,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      minTotal: filters.minTotal,
      maxTotal: filters.maxTotal,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 20,
      sortBy: filters.sortBy ?? 'CreatedAt',
      sortDirection: filters.sortDirection ?? 'desc'
    }
    return await apiClient.get<PaginatedResult<OrderListItem>>(
      `/shop/shops/${shopId}/orders`,
      { params }
    )
  },

  /**
   * Get pending orders (Paid, Confirmed, Processing)
   */
  async getPendingOrders(shopId: string): Promise<OrderListItem[]> {
    return await apiClient.get<OrderListItem[]>(`/shop/shops/${shopId}/orders/pending`)
  },

  /**
   * Get today's orders
   */
  async getTodayOrders(shopId: string): Promise<OrderListItem[]> {
    return await apiClient.get<OrderListItem[]>(`/shop/shops/${shopId}/orders/today`)
  },

  /**
   * Get order by ID
   */
  async getOrder(id: string): Promise<Order> {
    return await apiClient.get<Order>(`/shop/orders/${id}`)
  },

  async getOrderAuditTrail(id: string): Promise<OrderAuditEntry[]> {
    return await apiClient.get<OrderAuditEntry[]>(`/shop/orders/${id}/audit`)
  },

  /**
   * Update order status
   */
  async updateOrderStatus(id: string, data: UpdateOrderStatusRequest): Promise<Order> {
    return await apiClient.put<Order>(`/shop/orders/${id}/status`, data)
  },

  /**
   * Confirm an order
   */
  async confirmOrder(id: string, notes?: string): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/confirm`, { notes })
  },

  /**
   * Wholesale Custom WL — accept a PendingApproval order placed via the
   * public-catalog guest / not-yet-approved-buyer flow. BE moves the order
   * from PendingApproval → Confirmed. When `elevateToBuyer` is true, an
   * ApprovedBuyer row is also created for this customer so future orders
   * skip the approval queue.
   *
   * Distinct from `acceptOrder` above (that one is the standard shop-order
   * accept-into-processing action and hits /shop/orders/{id}/accept).
   */
  async acceptPendingOrder(id: string, elevateToBuyer: boolean = false): Promise<Order> {
    return await apiClient.post<Order>(`/provider/orders/${id}/accept`, { elevateToBuyer })
  },

  /**
   * Wholesale Custom WL — reject a PendingApproval order. BE moves it to
   * Cancelled with the supplied reason recorded on the order. Reason is
   * server-required (min 3 chars); dialog enforces the same client-side so
   * the user gets an inline error rather than a 400.
   */
  async rejectPendingOrder(id: string, reason: string): Promise<Order> {
    return await apiClient.post<Order>(`/provider/orders/${id}/reject`, { reason })
  },

  /**
   * Accept an order (simplified workflow - skips Confirmed, goes directly to Processing)
   */
  async acceptOrder(id: string, notes?: string): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/accept`, { notes })
  },

  /**
   * Start processing an order
   */
  async startProcessing(id: string, notes?: string): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/process`, { notes })
  },

  /**
   * Mark order as ready for collection/delivery
   */
  async markAsReady(id: string, notes?: string): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/ready`, { notes })
  },

  /**
   * Complete an order
   */
  async completeOrder(id: string, notes?: string): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/complete`, { notes })
  },

  /**
   * Cancel an order with optional refund processing.
   *
   * `refundMethod` + `refundReference` mirror `refundOrder` above and are only
   * consulted when `processRefund` is true. Default `Original` routes to
   * Stripe (unchanged legacy behaviour); manual methods (Cash/Card/
   * BankTransfer/Other) record a bookkeeping ledger row without hitting
   * Stripe — used when the operator picked a manual channel on the cancel
   * dialog's refund block.
   */
  async cancelOrder(
    id: string,
    reason?: string,
    processRefund: boolean = false,
    refundAmount?: number,
    refundMethod: 'Original' | 'Cash' | 'Card' | 'BankTransfer' | 'Other' = 'Original',
    refundReference?: string,
  ): Promise<CancelOrderResult> {
    return await apiClient.post<CancelOrderResult>(`/shop/orders/${id}/cancel`, {
      reason,
      processRefund,
      refundAmount,
      refundMethod,
      refundReference,
    })
  },

  /**
   * Process a refund for an order.
   *
   * `refundMethod` picks the payout channel:
   *   - `Original` (default) — routes to Stripe if the order still has
   *     Stripe capacity, otherwise BE returns 400 telling operator to
   *     choose a manual method.
   *   - `Cash` / `Card` / `BankTransfer` / `Other` — records a manual
   *     refund ledger row, no Stripe call. `refundReference` carries the
   *     bank ref (BankTransfer) or free-text note (Other).
   */
  async refundOrder(
    id: string,
    amount: number,
    reason?: string,
    refundMethod: 'Original' | 'Cash' | 'Card' | 'BankTransfer' | 'Other' = 'Original',
    refundReference?: string,
    restockItems?: RestockItem[],
  ): Promise<{
    order: Order
    refundId: string
    refundedAmount: number
  }> {
    return await apiClient.post(`/shop/orders/${id}/refund`, {
      amount,
      reason,
      refundMethod,
      refundReference,
      restockItems,
    })
  },

  /**
   * Collect payment for Pay on Delivery/Collection orders
   * @param id - Order ID
   * @param amountCollected - Amount collected (defaults to full amount due)
   * @param podPaymentMethod - How payment was collected: 'Cash', 'Card' or 'BankTransfer'
   */
  async collectPayment(
    id: string,
    amountCollected?: number,
    podPaymentMethod?: 'Cash' | 'Card' | 'BankTransfer'
  ): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/collect-payment`, {
      amountCollected,
      podPaymentMethod
    })
  },

  /**
   * Collect the extra balance owed on an order after a mid-flow amendment
   * pushed the total above what the customer paid. Provider records how
   * they took the money (Cash / Card / BankTransfer / Other) plus an
   * optional note; BE zeros out `extraToCollectAmount` and appends the
   * transaction to `paymentHistory`.
   */
  async collectExtraPayment(
    id: string,
    amount: number,
    method: 'Cash' | 'Card' | 'BankTransfer' | 'Other',
    note?: string
  ): Promise<Order> {
    return await apiClient.post<Order>(`/shop/orders/${id}/collect-extra-payment`, {
      amount,
      method,
      note
    })
  },

  /**
   * Send receipt email to customer or specified email
   * @param id - Order ID
   * @param email - Optional email override (uses customer email if not provided)
   */
  async sendReceipt(id: string, email?: string): Promise<{ message: string }> {
    return await apiClient.post<{ message: string }>(`/shop/orders/${id}/send-receipt`, { email })
  },

  // ============================================================================
  // Order Availability Issues (POD Orders Only)
  // ============================================================================

  /**
   * Contact customer about item availability issues (POD orders only)
   * Creates an availability issue and sends an email to the customer with accept/cancel options.
   */
  async contactCustomerAvailability(
    orderId: string,
    request: {
      items: Array<{
        orderItemId: string
        availableQuantity?: number
        isUnavailable: boolean
        alternativeProductId?: string
        alternativeVariantId?: string
        alternativeQuantity?: number
        alternativeNote?: string
        /** Option selections for the alternate. BE re-validates and folds
         *  each choice's PriceAdjustment into the alternate unit price. */
        alternativeOptionSelections?: Array<{ optionGroupId: string; optionChoiceId: string }>
      }>
      newLines?: Array<{
        productId: string
        productVariantId?: string
        quantity: number
        notes?: string
        /** Option selections applied to this new line. Server folds each
         *  choice's PriceAdjustment into the unit price and materialises
         *  the selections as OrderItemOptionSelection rows on accept. */
        optionSelections?: Array<{ optionGroupId: string; optionChoiceId: string }>
        /** True when this new line is an add-on. When set, productExtraId
         *  is required — the server uses ProductExtra.CalculatePrice for
         *  the unit price (matches storefront extras). */
        isExtra?: boolean
        productExtraId?: string
      }>
      message?: string
    }
  ): Promise<OrderAvailabilityIssue> {
    return await apiClient.post<OrderAvailabilityIssue>(
      `/shop/orders/${orderId}/contact-customer`,
      request
    )
  },

  /**
   * Amend an order immediately (provider has customer consent — e.g. on
   * a call). Same payload shape as contactCustomerAvailability, but
   * applies right away without the email/approval round-trip.
   */
  async amendOrder(
    orderId: string,
    request: {
      items: Array<{
        orderItemId: string
        availableQuantity?: number
        isUnavailable: boolean
        alternativeProductId?: string
        alternativeVariantId?: string
        alternativeQuantity?: number
        alternativeNote?: string
        alternativeOptionSelections?: Array<{ optionGroupId: string; optionChoiceId: string }>
      }>
      newLines?: Array<{
        productId: string
        productVariantId?: string
        quantity: number
        notes?: string
        optionSelections?: Array<{ optionGroupId: string; optionChoiceId: string }>
      }>
      message?: string
    }
  ): Promise<OrderAvailabilityIssue> {
    return await apiClient.post<OrderAvailabilityIssue>(
      `/shop/orders/${orderId}/amend`,
      request
    )
  },

  /**
   * Get the pending availability issue for an order, if any
   */
  async getPendingAvailabilityIssue(orderId: string): Promise<OrderAvailabilityIssueSummary | null> {
    return await apiClient.get<OrderAvailabilityIssueSummary | null>(
      `/shop/orders/${orderId}/availability-issue`
    )
  },

  /**
   * Full-detail variant of {@link getPendingAvailabilityIssue}. Returns the
   * complete pending issue with Items + NewLines + prices so the provider
   * order-detail page can render the "pending changes" banner without a
   * separate hop. Returns null when no pending issue exists.
   */
  async getPendingAvailabilityIssueDetail(orderId: string): Promise<OrderAvailabilityIssue | null> {
    return await apiClient.get<OrderAvailabilityIssue | null>(
      `/shop/orders/${orderId}/availability-issue/detail`
    )
  },

  /**
   * Get availability issue details by token (public endpoint for customer response page)
   */
  async getAvailabilityIssueByToken(token: string): Promise<AvailabilityIssuePublic> {
    return await apiClient.get<AvailabilityIssuePublic>(
      `/shop/orders/availability-response/${token}`
    )
  },

  /**
   * Respond to an availability issue (public endpoint for customer)
   * @param token - Response token from email
   * @param response - 'accept' or 'cancel'
   */
  async respondToAvailabilityIssue(
    token: string,
    response: 'accept' | 'cancel'
  ): Promise<AvailabilityResponseResult> {
    return await apiClient.post<AvailabilityResponseResult>(
      `/shop/orders/availability-response/${token}`,
      { response }
    )
  },

  /**
   * Get order statistics
   */
  async getOrderStatistics(
    shopId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<OrderStatistics> {
    const params: Record<string, string> = {}
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    return await apiClient.get<OrderStatistics>(
      `/shop/shops/${shopId}/orders/statistics`,
      { params }
    )
  },

  /**
   * Get total revenue
   */
  async getRevenue(
    shopId: string,
    fromDate?: string,
    toDate?: string
  ): Promise<{ totalRevenue: number }> {
    const params: Record<string, string> = {}
    if (fromDate) params.fromDate = fromDate
    if (toDate) params.toDate = toDate
    return await apiClient.get(`/shop/shops/${shopId}/orders/revenue`, { params })
  },

  // ============================================================================
  // Customer Checkout
  // ============================================================================

  /**
   * Create a new order
   */
  async createOrder(data: CreateOrderRequest): Promise<Order> {
    return await apiClient.post<Order>('/checkout/orders', data)
  },

  /**
   * Get order by order number (for authenticated customers or guest lookup with email)
   */
  async getOrderByNumber(orderNumber: string, email?: string): Promise<Order> {
    const params = email ? { email } : undefined
    return await apiClient.get<Order>(`/customer/orders/${orderNumber}`, { params })
  },

  /**
   * Create payment intent for an order
   */
  async createPaymentIntent(orderId: string): Promise<PaymentIntentResult> {
    return await apiClient.post<PaymentIntentResult>(`/checkout/orders/${orderId}/payment-intent`)
  },

  /**
   * Confirm payment was successful
   */
  async confirmPayment(orderId: string, paymentIntentId: string): Promise<Order> {
    return await apiClient.post<Order>(`/checkout/orders/${orderId}/confirm-payment`, {
      paymentIntentId
    })
  },

  /**
   * Get authenticated customer's order history
   */
  async getCustomerOrders(
    status?: OrderStatus,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResult<OrderListItem>> {
    const params: Record<string, any> = { page, pageSize }
    if (status) params.status = status
    return await apiClient.get<PaginatedResult<OrderListItem>>('/customer/orders', { params })
  },

  // ============================================================================
  // Helper Functions
  // ============================================================================

  /**
   * Operational status label for the Status column. Reads as the shop
   * operator would describe the order out loud — NOT the payment status.
   * Payment status is a separate concern (see paymentStatus + its label).
   *
   * Rules:
   *   Paid / PartiallyPaid / Unpaid (pre-acceptance)  → "New"
   *     — the payment sub-state (online paid vs POD deposit vs POD unpaid)
   *       is orthogonal to the operational state; before the shop accepts,
   *       ALL of these mean "just came in, needs Accept".
   *   Confirmed                       → "Accepted"
   *   Processing                      → "Preparing"
   *   ReadyForCollection              → "Ready for Collection"
   *   OutForDelivery                  → "Out for Delivery"
   *   Completed + Delivery type       → "Delivered"
   *   Completed + Collection type     → "Collected"
   *   Completed (unknown fulfillment) → "Completed"
   *   AwaitingCustomerResponse        → "Awaiting Response"
   *   PendingApproval                 → "Awaiting Approval"
   *   Cancelled / Refunded / etc      → literal
   *
   * `fulfillmentType` optional — omit for lists that don't have the row
   * context; the label still reads correctly, just falls back to
   * "Completed" instead of "Delivered/Collected".
   */
  getStatusLabel(status: OrderStatus, fulfillmentType?: string): string {
    switch (status) {
      case 'Draft':
        return 'Draft'
      case 'PendingPayment':
        return 'Pending Payment'
      case 'PaymentFailed':
        return 'Payment Failed'
      // Pre-acceptance states — one operational label regardless of how
      // the money side is sitting.
      case 'Paid':
      case 'PartiallyPaid':
      case 'Unpaid':
        return 'New'
      case 'AwaitingCustomerResponse':
        return 'Awaiting Response'
      case 'PendingApproval':
        return 'Awaiting Approval'
      case 'Confirmed':
        return 'Accepted'
      case 'Processing':
        return 'Preparing'
      case 'ReadyForCollection':
        return 'Ready for Collection'
      case 'OutForDelivery':
        return 'Out for Delivery'
      case 'Completed':
        if (fulfillmentType === 'Delivery') return 'Delivered'
        if (fulfillmentType === 'Collection') return 'Collected'
        return 'Completed'
      case 'Cancelled':
        return 'Cancelled'
      case 'Refunded':
        return 'Refunded'
      case 'PartiallyRefunded':
        return 'Partially Refunded'
      case 'Abandoned':
        return 'Abandoned'
      default:
        return 'Unknown'
    }
  },

  getStatusColor(status: OrderStatus): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    switch (status) {
      case 'Draft':
        return 'secondary'
      case 'PendingPayment':
        return 'warn'
      case 'PaymentFailed':
        return 'danger'
      case 'Paid':
        return 'info'
      case 'PartiallyPaid':
        return 'info'
      case 'Unpaid':
        return 'warn'
      case 'AwaitingCustomerResponse':
        return 'warn'
      case 'Confirmed':
        return 'info'
      case 'Processing':
        return 'info'
      case 'ReadyForCollection':
        return 'success'
      case 'OutForDelivery':
        return 'success'
      case 'Completed':
        return 'success'
      case 'Cancelled':
        return 'danger'
      case 'Refunded':
        return 'secondary'
      case 'PartiallyRefunded':
        return 'warn'
      case 'Abandoned':
        return 'secondary'
      default:
        return 'secondary'
    }
  },

  getFulfillmentLabel(type: FulfillmentType): string {
    switch (type) {
      case 'Collection':
        return 'Collection'
      case 'Delivery':
        return 'Delivery'
      default:
        return 'Unknown'
    }
  },

  getFulfillmentIcon(type: FulfillmentType): string {
    switch (type) {
      case 'Collection':
        return 'pi pi-shopping-bag'
      case 'Delivery':
        return 'pi pi-truck'
      default:
        return 'pi pi-box'
    }
  },

  /**
   * Get the primary next action for an order based on status and fulfillment type.
   * This is for the simplified "smart action" workflow.
   *
   * For POD orders that are ready for collection/delivery, the workflow requires
   * payment collection before completing:
   * - Collection: ReadyForCollection → Collect Payment → Mark Collected
   * - Delivery: OutForDelivery → Collect Payment → Mark Delivered
   */
  getNextAction(
    status: OrderStatus,
    fulfillmentType: FulfillmentType,
    paymentInfo?: { paymentMethod: 'Online' | 'PayOnDelivery' | 'InvoiceLater'; amountDue: number }
  ): {
    action: 'accept' | 'process' | 'ready' | 'complete' | 'collectPayment' | null
    label: string
    icon: string
    severity: 'success' | 'primary' | 'info' | 'warn'
  } | null {
    switch (status) {
      case 'Paid':
      case 'PartiallyPaid':
      case 'Unpaid':
        return {
          action: 'accept',
          label: 'Accept Order',
          icon: 'pi pi-check',
          severity: 'success'
        }
      case 'Confirmed':
        return {
          action: 'process',
          label: 'Start Preparing',
          icon: 'pi pi-cog',
          severity: 'primary'
        }
      case 'Processing':
        return {
          action: 'ready',
          label: fulfillmentType === 'Collection' ? 'Ready for Collection' : 'Out for Delivery',
          icon: fulfillmentType === 'Collection' ? 'pi pi-shopping-bag' : 'pi pi-truck',
          severity: 'success'
        }
      case 'ReadyForCollection':
        // For POD orders with balance due, collect payment before completing
        if (paymentInfo?.paymentMethod === 'PayOnDelivery' && paymentInfo.amountDue > 0) {
          return {
            action: 'collectPayment',
            label: 'Collect Payment',
            icon: 'pi pi-wallet',
            severity: 'warn'
          }
        }
        return {
          action: 'complete',
          label: 'Mark Collected',
          icon: 'pi pi-check-circle',
          severity: 'success'
        }
      case 'OutForDelivery':
        // For POD orders with balance due, collect payment before completing
        if (paymentInfo?.paymentMethod === 'PayOnDelivery' && paymentInfo.amountDue > 0) {
          return {
            action: 'collectPayment',
            label: 'Collect Payment',
            icon: 'pi pi-wallet',
            severity: 'warn'
          }
        }
        return {
          action: 'complete',
          label: 'Mark Delivered',
          icon: 'pi pi-check-circle',
          severity: 'success'
        }
      default:
        return null
    }
  },

  /**
   * Get next available actions for an order
   */
  getAvailableActions(status: OrderStatus): Array<{
    action: 'confirm' | 'process' | 'ready' | 'complete' | 'cancel' | 'refund'
    label: string
    icon: string
    severity: 'primary' | 'success' | 'warn' | 'danger' | 'secondary'
  }> {
    const actions: Array<{
      action: 'confirm' | 'process' | 'ready' | 'complete' | 'cancel' | 'refund'
      label: string
      icon: string
      severity: 'primary' | 'success' | 'warn' | 'danger' | 'secondary'
    }> = []

    switch (status) {
      case 'Paid':
      case 'PartiallyPaid':
      case 'Unpaid':
        actions.push(
          { action: 'confirm', label: 'Confirm Order', icon: 'pi pi-check', severity: 'primary' },
          { action: 'cancel', label: 'Cancel Order', icon: 'pi pi-times', severity: 'danger' }
        )
        break
      case 'Confirmed':
        actions.push(
          { action: 'process', label: 'Start Processing', icon: 'pi pi-cog', severity: 'primary' },
          { action: 'cancel', label: 'Cancel Order', icon: 'pi pi-times', severity: 'danger' }
        )
        break
      case 'Processing':
        actions.push(
          { action: 'ready', label: 'Mark as Ready', icon: 'pi pi-check-circle', severity: 'success' },
          { action: 'cancel', label: 'Cancel Order', icon: 'pi pi-times', severity: 'danger' }
        )
        break
      case 'ReadyForCollection':
      case 'OutForDelivery':
        actions.push(
          { action: 'complete', label: 'Complete Order', icon: 'pi pi-check-circle', severity: 'success' }
        )
        break
      case 'Completed':
        actions.push(
          { action: 'refund', label: 'Process Refund', icon: 'pi pi-replay', severity: 'warn' }
        )
        break
    }

    return actions
  },

  /**
   * Check if order can be cancelled
   */
  canCancel(status: OrderStatus): boolean {
    return ['Paid', 'PartiallyPaid', 'Unpaid', 'Confirmed', 'Processing'].includes(status)
  },

  /**
   * Check if order can be refunded. Mirrors Order.CanBeRefunded() on the
   * backend — any status where money is collected and the order isn't
   * cancelled/refunded is eligible for a (partial or full) refund via the
   * standalone Refund button. The button is what unlocks partial refunds
   * without forcing the provider through the Cancel flow.
   */
  canRefund(status: OrderStatus): boolean {
    return ['Paid', 'Confirmed', 'Processing', 'Completed', 'PartiallyRefunded'].includes(status)
  },

  /**
   * Format order date for display in the shop's wall-clock. Pass the shop's
   * regionCode so PK shops see PKT and GB shops see BST/GMT; unknown → GB.
   */
  formatOrderDate(dateString: string, regionCode?: string | null): string {
    return fmtDate(
      dateString,
      { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
      getTimeZoneIdForRegion(regionCode),
    )
  },

  /**
   * Get time since order was placed
   */
  getTimeSinceOrder(dateString: string): string {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    } else if (diffMins > 0) {
      return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
    } else {
      return 'Just now'
    }
  }
}
